/**
 * 報表用的通用表格(REQ-20260815002)。
 *
 * 九張報表的欄位各不相同,但**排序、三態、總計列的行為必須一致** ——
 * 各寫一份的下場是每張表的排序箭頭長得不一樣、有的忘了空狀態。
 * 樣式與排序互動沿用 `.claude/skills/ui-guidelines` §1 / §2 / §4。
 *
 * 排序一律在前端做:報表是一次撈完的小清單(§2「資料已一次全載入 → 前端排序」),
 * 再打一次 API 只是把同一份資料重排。
 */
import { useMemo, useState, type ReactNode } from 'react';

/**
 * 欄位對齊(REQ-20260831009 加上 `'center'`)。
 * 未指定就是靠左 —— 這是九張報表絕大多數欄位的樣子,不要改預設值。
 */
export type ReportAlign = 'right' | 'center';

/**
 * thead / tbody / tfoot 三處共用的對齊 class(REQ-20260831009)。
 * REQ-20260901002 起 `ArDetailPrintPages` 也用它 —— **只是 export 出去重用,
 * 產生的字串一個字都沒改**,否則等於一次改九張報表。
 * 未指定 align 時回空字串,所以**不傳或傳 `'right'` 時產生的 class 與加 `'center'`
 * 之前一字不差** —— `ReportTable` 是九張報表共用的(REQ-20260815002),
 * 帳款明細表要置中不該讓另外八張跟著動。
 */
export function alignClass(align?: ReportAlign): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return '';
}

/**
 * 表身 / 合計列的儲存格:靠右的數字欄**同時**吃等寬字型(既有行為,沒有改)。
 * `font-mono` 刻意只跟著 `'right'` —— 讓它也跟著 `'center'` 會把日期 / 單位這種
 * 文字欄一起換成等寬字型,連帶改變欄寬。置中又要等寬的欄位請自己給
 * `className: 'font-mono'`(帳款明細表的四個金額欄就是這樣做的)。
 */
export function cellClass(align?: ReportAlign): string {
  return align === 'right' ? 'text-right font-mono' : alignClass(align);
}

export interface ReportColumn<T> {
  key: string;
  label: string;
  /** 金額 / 數字欄靠右;`'center'` 是置中,未指定就靠左 */
  align?: ReportAlign;
  /** 排序用的值。回數字就走數值排序,回字串走 `localeCompare('zh-Hant')` */
  value: (row: T) => string | number;
  /** 顯示內容,省略時直接顯示 `value()` */
  render?: (row: T) => ReactNode;
  /** 預設可排序;明確給 false 才關掉(例如「沖到的單號」這種多值欄) */
  sortable?: boolean;
  className?: string;
}

export interface ReportTableProps<T> {
  columns: Array<ReportColumn<T>>;
  rows: T[];
  rowKey: (row: T) => string | number;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** 空清單時要說「接下來怎麼辦」,不是只留一張空表(ui-guidelines §4) */
  emptyHint: string;
  /** 底部總計列:與 columns 等長,null 代表該欄不顯示合計 */
  footer?: Array<ReactNode | null>;
  /**
   * 表頭列額外的 class(REQ-20260831008)。**不傳時表頭與原本完全一樣** ——
   * 這支表格是九張報表共用的,帳款明細表要縮表頭字級不該讓另外八張跟著變。
   * 要壓 `<th>` 自己的 UA 樣式(粗體)得用 `[&_th]:` 這種 arbitrary variant,
   * 掛在 `<tr>` 上靠繼承壓不掉。
   */
  headClassName?: string;
}

export function ReportTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  isError,
  error,
  emptyHint,
  footer,
  headClassName,
}: ReportTableProps<T>): JSX.Element {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    // 沒點過排序就維持後端給的順序(各報表已經照最有用的欄位排好了)
    if (!col) return rows;
    const sign = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = col.value(a);
      const bv = col.value(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign;
      return String(av).localeCompare(String(bv), 'zh-Hant') * sign;
    });
  }, [rows, columns, sortKey, sortDir]);

  const onSort = (key: string): void => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (isLoading) {
    return (
      <div role="status" className="text-slate-500 text-sm">
        載入中...
      </div>
    );
  }
  if (isError) {
    return (
      <div role="alert" className="text-red-600 text-sm">
        載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
      </div>
    );
  }
  if (rows.length === 0) {
    return <div className="text-slate-500 text-sm">{emptyHint}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full bg-white shadow-sm rounded-md">
        <thead>
          <tr
            className={`border-b text-left text-sm text-slate-600${
              headClassName ? ` ${headClassName}` : ''
            }`}
          >
            {columns.map((c) =>
              c.sortable === false ? (
                <th key={c.key} className={`px-4 py-2 ${alignClass(c.align)}`}>
                  {c.label}
                </th>
              ) : (
                <th key={c.key} className={`px-4 py-2 ${alignClass(c.align)}`}>
                  <button
                    type="button"
                    onClick={() => onSort(c.key)}
                    aria-label={`依${c.label}排序`}
                    className="inline-flex items-center gap-1 hover:text-blue-600"
                  >
                    {c.label}
                    <span
                      className={
                        sortKey === c.key ? 'text-blue-600 text-xs' : 'text-slate-300 text-xs'
                      }
                    >
                      {sortKey === c.key && sortDir === 'asc' ? '▲' : '▼'}
                    </span>
                  </button>
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={rowKey(row)} className="border-b last:border-0 hover:bg-slate-50 text-sm">
              {columns.map((c) => (
                <td key={c.key} className={`px-4 py-2 ${cellClass(c.align)} ${c.className ?? ''}`}>
                  {c.render ? c.render(row) : c.value(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer ? (
          <tfoot>
            <tr className="border-t-2 text-sm font-medium bg-slate-50">
              {columns.map((c, i) => (
                <td
                  key={c.key}
                  // 合計列也吃 c.className(REQ-20260831009):置中的金額欄靠 className
                  // 補等寬字型,合計列不跟著吃的話會與同列靠右的欄位字型不一致
                  className={`px-4 py-2 ${cellClass(c.align)}${
                    c.className ? ` ${c.className}` : ''
                  }`}
                >
                  {footer[i] ?? null}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
